import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, ShieldCheck } from "lucide-react";
import { enableMetaPixel, revokeMetaPixel, trackMetaPageView } from "../lib/metaPixel";

const STORAGE_KEY = "mp-cookie-consent";
const GA_ID = "G-WGWT44HQSP";

// SECURITY NOTE: localStorage is used here to remember a single, NON-SENSITIVE
// boolean choice ("accepted" or "rejected" for analytics cookies). It contains
// no personal data, no tokens and no identifiers, so an XSS attack reading this
// key has no exploit value. We deliberately do not use a cookie because:
//   1. The choice is purely client-side (we only load GA4 if accepted).
//   2. Storing it in localStorage avoids sending another header on every request.
//   3. UK/EU PECR & GDPR guidance treats this as a consent-preference signal,
//      not personal data.

// Inject GA4 once, after consent is granted.
const loadGA4 = () => {
  if (typeof window === "undefined") return;
  if (window.__mpGaLoaded) return;
  window.__mpGaLoaded = true;

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", GA_ID, { anonymize_ip: true });
};

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  // STORAGE_KEY, loadGA4, GA_ID are stable module-level constants — safe to omit.
  useEffect(() => {
    const choice = (typeof window !== "undefined") ? localStorage.getItem(STORAGE_KEY) : null;
    if (choice === "accepted") {
      if (typeof window !== "undefined") window[`ga-disable-${GA_ID}`] = false;
      loadGA4();
      // Marketing: the same accept also enables the Meta advertising pixel.
      enableMetaPixel();
      trackMetaPageView(typeof window !== "undefined" ? window.location.pathname : "");
      return;
    }
    if (choice === "rejected") {
      // Belt-and-braces: ensure GA is disabled even if a script ever loads.
      if (typeof window !== "undefined") window[`ga-disable-${GA_ID}`] = true;
      revokeMetaPixel();
      return;
    }
    // No choice yet — show banner shortly after page load
    const t = setTimeout(() => setVisible(true), 600);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Allow the footer "Cookie settings" link to reopen the consent controls at
  // any time so visitors can change/withdraw their Google Analytics choice
  // without clearing cookies or local storage.
  useEffect(() => {
    const openSettings = () => setVisible(true);
    window.addEventListener("mp:open-cookie-settings", openSettings);
    return () => window.removeEventListener("mp:open-cookie-settings", openSettings);
  }, []);

  const accept = () => {
    // Stores only the non-sensitive consent choice ("accepted"/"rejected") —
    // no personal data ever goes into localStorage.
    localStorage.setItem(STORAGE_KEY, "accepted");
    window[`ga-disable-${GA_ID}`] = false;
    loadGA4();
    // Marketing: enable the Meta advertising pixel and record this PageView.
    enableMetaPixel();
    trackMetaPageView(window.location.pathname);
    setVisible(false);
  };
  const reject = () => {
    localStorage.setItem(STORAGE_KEY, "rejected");
    // Withdraw consent: stop GA4 and the Meta pixel sending any further hits.
    window[`ga-disable-${GA_ID}`] = true;
    revokeMetaPixel();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie preferences"
      data-testid="cookie-consent-banner"
      className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4 sm:px-6 sm:pb-6 pointer-events-none"
    >
      <div className="pointer-events-auto mx-auto max-w-3xl rounded-2xl border border-[#DCE7E3] bg-white shadow-[0_10px_40px_rgba(11,18,32,0.15)] p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <span className="w-9 h-9 shrink-0 rounded-lg bg-[#0F766E]/10 grid place-items-center">
            <Cookie className="w-4.5 h-4.5 text-[#0F766E]" />
          </span>
          <div className="flex-1">
            <div className="font-sora text-sm font-semibold text-[#0B1220] flex items-center gap-2">
              We use a few cookies <ShieldCheck className="w-3.5 h-3.5 text-[#10B981]" />
            </div>
            <p className="mt-1.5 text-[13px] text-[#475569] leading-relaxed">
              Strictly-necessary cookies keep the site working. With your permission we also load
              {" "}<strong className="text-[#0B1220]">Google Analytics 4</strong> (anonymised) and the
              {" "}<strong className="text-[#0B1220]">Meta (Facebook) advertising pixel</strong>, which help us
              understand how visitors use MortgagePath and measure our advertising. Nothing non-essential loads
              until you accept. You can change or withdraw your choice any time via the
              {" "}<Link to="/cookies" className="text-[#0F766E] hover:underline">Cookie Policy</Link>.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={accept}
                className="inline-flex items-center justify-center gap-2 bg-[#0F766E] text-white hover:bg-[#0B5F58] font-semibold rounded-full px-5 py-2 text-sm transition-colors"
                data-testid="cookie-accept"
              >
                Accept cookies
              </button>
              <button
                onClick={reject}
                className="inline-flex items-center justify-center gap-2 bg-white text-[#0F766E] border border-[#DCE7E3] hover:border-[#0F766E] font-semibold rounded-full px-5 py-2 text-sm transition-colors"
                data-testid="cookie-reject"
              >
                Reject non-essential
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
